import { useState, useEffect, useCallback } from 'react';
import { vendorService, type InsuranceExpiryRow } from '../../../services/vendorService';

export interface UseInsuranceExpiriesResult {
  rows: InsuranceExpiryRow[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Matches the server default; stated here so the caller can see the window it is asking for. */
export const INSURANCE_EXPIRY_WINDOW_DAYS = 30;

/**
 * HE-M18 widget: vendor WCB, liability insurance and per-resource vehicle insurance that
 * expire within the window, plus anything already lapsed. Already sorted most urgent first
 * by the server, so this hook does not re-sort.
 *
 * `enabled` is driven by the widget permission at the call site, so a user without it never
 * issues the request.
 */
export function useInsuranceExpiries(
  enabled: boolean,
  withinDays: number = INSURANCE_EXPIRY_WINDOW_DAYS
): UseInsuranceExpiriesResult {
  const [rows, setRows] = useState<InsuranceExpiryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchRows = useCallback(async () => {
    if (!enabled) {
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      setRows(await vendorService.listInsuranceExpiries(withinDays));
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to fetch upcoming insurance expiries')
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, withinDays]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  return { rows, loading, error, refetch: fetchRows };
}
